import { executeGraphqlRequest } from "../apollo/graphql-request";

export interface AuthAccount {
  uuid: string;
  username: string;
  authProvider?: "LOCAL" | "GOOGLE" | "APPLE" | "TELEGRAM" | null;
  profiles?: AuthProfile[];
}

export interface AuthProfile {
  uuid: string;
  title: string;
  firstName?: string | null;
  lastName?: string | null;
  description?: string | null;
  avatarUrl?: string | null;
  createdAt?: string | null;
}

export interface AuthPayload {
  accessToken: string;
  account: AuthAccount;
  isNewAccount: boolean;
}

interface AuthPayloadResponse {
  accessToken: string;
  account: AuthAccount;
  isNewAccount?: boolean;
}

export interface SignInInput {
  username: string;
  password: string;
}

export interface SignUpInput {
  firstName: string;
  lastName: string;
  username: string;
  password: string;
}

export interface SignInWithGoogleInput {
  idToken: string;
}

export interface SignInWithAppleInput {
  firstName?: string;
  idToken: string;
  lastName?: string;
}

async function executeAuthRequest<TData>(
  query: string,
  variables: Record<string, unknown>
): Promise<TData> {
  return executeGraphqlRequest<TData>(query, variables, {
    requestName: "Authentication",
  });
}

function normalizeAuthPayload(
  payload: AuthPayloadResponse,
  defaultIsNewAccount: boolean
): AuthPayload {
  return {
    ...payload,
    isNewAccount:
      typeof payload.isNewAccount === "boolean" ? payload.isNewAccount : defaultIsNewAccount,
  };
}

const AUTH_ACCOUNT_SELECTION_SET = `
  uuid
  username
  authProvider
  profiles {
    uuid
    title
    firstName
    lastName
    description
    avatarUrl
    createdAt
  }
`;

export async function signInRequest(input: SignInInput): Promise<AuthPayload> {
  const response = await executeAuthRequest<{ signIn: AuthPayloadResponse }>(
    `
      mutation SignIn($input: SignInInput!) {
        signIn(input: $input) {
          accessToken
          account {
            ${AUTH_ACCOUNT_SELECTION_SET}
          }
        }
      }
    `,
    { input }
  );

  return normalizeAuthPayload(response.signIn, false);
}

export async function signUpRequest(input: SignUpInput): Promise<AuthPayload> {
  const response = await executeAuthRequest<{ signUp: AuthPayloadResponse }>(
    `
      mutation SignUp($input: SignUpInput!) {
        signUp(input: $input) {
          accessToken
          account {
            ${AUTH_ACCOUNT_SELECTION_SET}
          }
        }
      }
    `,
    { input }
  );

  return normalizeAuthPayload(response.signUp, true);
}

export async function signInWithGoogleRequest(input: SignInWithGoogleInput): Promise<AuthPayload> {
  const response = await executeAuthRequest<{ signInWithGoogle: AuthPayloadResponse }>(
    `
      mutation SignInWithGoogle($input: SignInWithGoogleInput!) {
        signInWithGoogle(input: $input) {
          accessToken
          account {
            ${AUTH_ACCOUNT_SELECTION_SET}
          }
        }
      }
    `,
    { input }
  );

  return normalizeAuthPayload(response.signInWithGoogle, false);
}

export async function signInWithAppleRequest(input: SignInWithAppleInput): Promise<AuthPayload> {
  const response = await executeAuthRequest<{ signInWithApple: AuthPayloadResponse }>(
    `
      mutation SignInWithApple($input: SignInWithAppleInput!) {
        signInWithApple(input: $input) {
          accessToken
          account {
            ${AUTH_ACCOUNT_SELECTION_SET}
          }
        }
      }
    `,
    { input }
  );

  return normalizeAuthPayload(response.signInWithApple, false);
}
